import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Eye, EyeOff, ImageUp, Loader2, Plus, Trash2, Save, ImageOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Badges";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function EntryRow({ e, index, total, onMove, onChanged, onDelete }) {
  const [f, setF] = useState({ name: e.name, city: e.city, state: e.state });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const dirty = f.name !== e.name || f.city !== e.city || f.state !== e.state;

  const save = async (patch = {}) => {
    try {
      const { data } = await api.put(`/admin/showcase/${e.id}`, { ...f, hidden: e.hidden, ...patch });
      onChanged(data);
      toast.success(`${data.name} updated`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/showcase/${e.id}/photo`, fd);
      onChanged(data);
      toast.success("Photo replaced");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      fileRef.current.value = "";
    }
  };

  return (
    <div className={`flex flex-col gap-4 p-4 md:flex-row md:items-center ${e.hidden ? "bg-zinc-50" : ""}`} data-testid={`showcase-row-${e.id}`}>
      <div className="relative h-20 w-32 shrink-0 overflow-hidden rounded-xl bg-lasso-tint">
        {e.photo_url ? <img src={`${BACKEND}${e.photo_url}`} alt={e.name} className="h-full w-full object-cover" data-testid={`showcase-thumb-${e.id}`} /> : <div className="flex h-full items-center justify-center text-zinc-400"><ImageOff className="h-5 w-5" /></div>}
        {uploading && <div className="absolute inset-0 flex items-center justify-center bg-white/70"><Loader2 className="h-5 w-5 animate-spin" /></div>}
      </div>
      <div className="grid flex-1 gap-2 sm:grid-cols-[1fr_1fr_70px]">
        <Input value={f.name} onChange={(ev) => setF({ ...f, name: ev.target.value })} data-testid={`showcase-name-${e.id}`} />
        <Input value={f.city} placeholder="City" onChange={(ev) => setF({ ...f, city: ev.target.value })} data-testid={`showcase-city-${e.id}`} />
        <Input value={f.state} placeholder="ST" maxLength={2} onChange={(ev) => setF({ ...f, state: ev.target.value.toUpperCase() })} data-testid={`showcase-state-${e.id}`} />
        {e.has_photo && (
          <p className="text-[11px] leading-snug text-zinc-500 sm:col-span-3" data-testid={`showcase-attribution-${e.id}`}>
            Photo: {e.photo_credit || "Unknown source"}{e.photo_license ? ` · ${e.photo_license}` : ""}
            {e.photo_page_url && <> · <a href={e.photo_page_url} target="_blank" rel="noreferrer" className="underline hover:text-lasso-ink">source page</a></>}
            {e.photo_note && <span className="block text-amber-700">{e.photo_note}</span>}
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {dirty && <button onClick={() => save()} className="rounded-md p-2 text-lasso-deep hover:bg-lasso-tint" title="Save" data-testid={`showcase-save-${e.id}`}><Save className="h-4 w-4" /></button>}
        <button disabled={index === 0} onClick={() => onMove(-1)} className="rounded-md p-2 text-zinc-500 hover:bg-lasso-tint disabled:opacity-30" title="Move up" data-testid={`showcase-up-${e.id}`}><ArrowUp className="h-4 w-4" /></button>
        <button disabled={index === total - 1} onClick={() => onMove(1)} className="rounded-md p-2 text-zinc-500 hover:bg-lasso-tint disabled:opacity-30" title="Move down" data-testid={`showcase-down-${e.id}`}><ArrowDown className="h-4 w-4" /></button>
        <button onClick={() => (!e.has_photo && e.hidden ? toast.error("Add a photo before showing this community") : save({ hidden: !e.hidden }))} className="rounded-md p-2 text-zinc-500 hover:bg-lasso-tint" title={e.hidden ? "Show" : "Hide"} data-testid={`showcase-visibility-${e.id}`}>{e.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
        <button onClick={() => fileRef.current.click()} className="rounded-md p-2 text-zinc-500 hover:bg-lasso-tint" title={e.has_photo ? "Replace photo" : "Upload photo"} data-testid={`showcase-photo-${e.id}`}><ImageUp className="h-4 w-4" /></button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(ev) => upload(ev.target.files[0])} data-testid={`showcase-photo-input-${e.id}`} />
        <button onClick={() => window.confirm(`Remove ${e.name} from the showcase?`) && onDelete()} className="rounded-md p-2 text-zinc-500 hover:bg-rose-50 hover:text-rose-600" title="Delete" data-testid={`showcase-delete-${e.id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export default function Showcase() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({ name: "", city: "", state: "" });

  const load = useCallback(() => api.get("/admin/showcase").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const replace = (row) => setData((d) => ({ ...d, items: d.items.map((x) => (x.id === row.id ? row : x)) }));
  const move = async (i, dir) => {
    const next = [...items];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    setData((d) => ({ ...d, items: next }));
    await api.post("/admin/showcase/reorder", { ids: next.map((x) => x.id) }).catch((e) => toast.error(formatApiError(e)));
  };
  const remove = async (id) => {
    await api.delete(`/admin/showcase/${id}`);
    setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== id) }));
  };
  const add = async () => {
    if (!draft.name.trim()) return toast.error("Community name is required");
    try {
      await api.post("/admin/showcase", { ...draft, hidden: true });
      setDraft({ name: "", city: "", state: "" });
      toast.success("Added — upload a photo, then make it visible");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const visible = items.filter((x) => !x.hidden && x.has_photo).length;
  return (
    <div className="fade-up">
      <PageHeader eyebrow="Admin" title="Community showcase" description={`The scrolling strip of communities on the landing page. ${visible} of ${items.length} visible.`} />
      {data?.seed?.failed?.length > 0 && (
        <p className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="showcase-seed-missing">
          No photo could be found on lassocommunities.com for: {data.seed.failed.join(", ")}. They're hidden until you upload a photo.
        </p>
      )}
      <div className="mb-6 flex flex-col gap-2 rounded-2xl border border-lasso-line bg-white p-4 sm:flex-row">
        <Input placeholder="Community name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="showcase-new-name" />
        <Input placeholder="City" value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} data-testid="showcase-new-city" />
        <Input placeholder="ST" maxLength={2} className="sm:w-20" value={draft.state} onChange={(e) => setDraft({ ...draft, state: e.target.value.toUpperCase() })} data-testid="showcase-new-state" />
        <button onClick={add} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-lasso-ink px-5 py-2 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid="showcase-add-button"><Plus className="h-4 w-4" /> Add</button>
      </div>
      <div className="divide-y divide-lasso-line overflow-hidden rounded-2xl border border-lasso-line bg-white" data-testid="showcase-list">
        {items.map((e, i) => <EntryRow key={e.id} e={e} index={i} total={items.length} onMove={(d) => move(i, d)} onChanged={replace} onDelete={() => remove(e.id)} />)}
      </div>
    </div>
  );
}
